<template>
  <div
    class="rangeslide-filter"
    v-bind:class="{
      'standalone-filter': standaloneFilter,
      'grey lighten-3': filterIsActive,
    }"
  >
    <v-row class="mb-0 pb-0 mt-0" justify="start" align="center">
      <v-col cols="auto" class="mb-0 pa-0 pl-2">
        <v-checkbox
          class="pt-0 mt-0 pr-0 mr-0"
          v-bind:class="{
            'range-filter-checkbox': !standaloneFilter || filterIsActive,
          }"
          v-if="showCheckbox"
          @change="filterActiveChanged"
          v-model="filterIsActive"
          :label="title"
          hide-details
        />
      </v-col>
      <v-col justify="end" class="mb-0 pa-0">
        <filter-title
          :show-title="false"
          :show-reset-button="filterIsActive"
          :show-settings-button="filterIsActive"
          @filter-reset="resetFilterFromButton"
          @toggle-description="showDescription = !showDescription"
          @toggle-settings="showSettings = !showSettings"
          :title="title"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col class="pa-0" v-bind:class="{ 'pr-5': standaloneFilter }">
        <filter-description
          :title="title"
          :show="showDescription"
          :description="description"
        />
      </v-col>
    </v-row>

    <div v-if="filterIsActive">
      <form @submit.prevent="submitFilter">
        <v-row v-show="showSettings">
          <v-col class="pb-0">
            <filter-settings
              class="pr-4"
              :show="showSettings"
              :items="filterMode.options"
              :value="filterMode.value"
              @setting-changed="selectModeChanged"
            />
          </v-col>
        </v-row>

        <v-row
          class="ml-0 mt-0"
          v-bind:class="{
            'filter-range-width-standalone': standaloneFilter,
            'filter-range-width': !standaloneFilter,
          }"
          justify="start"
          align="start"
        >
          <v-col class="mr-2 pl-0 pr-0">
            <v-text-field
              class="range-filter-checkbox"
              type="number"
              v-model="textInputMin"
              dense
              background-color="white"
              solo
              flat
              persistent-hint
              min="0"
              :step="options.decimals ? 0.001 : 1"
              :max="fromTextFieldMax"
              :aria-label="title + ' Minimum'"
              :label="
                filterMode.value.id === 'range' ? 'Min' : 'Skriv inn verdi'
              "
              :hint="
                filterMode.value.id === 'range'
                  ? 'Min: 0 ' + options.unit
                  : 'Minimum: 0, Maksimum: ' + options.max
              "
            ></v-text-field>
          </v-col>

          <v-col class="pl-0 pr-0" v-if="filterMode.value.id === 'range'">
            <v-text-field
              class="range-filter-checkbox"
              type="number"
              v-model="textInputMax"
              dense
              solo
              flat
              background-color="white"
              persistent-hint
              :min="toTextFieldMin"
              :step="options.decimals ? 0.001 : 1"
              :max="options.max"
              :aria-label="title + ' Maksimum'"
              label="Maks."
              :hint="'Maks: ' + options.max + ' ' + options.unit"
            ></v-text-field>
          </v-col>

          <v-col>
            <v-tooltip :disabled="!okButtonDisabled" :open-delay="500" top>
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on">
                  <v-btn
                    :disabled="okButtonDisabled"
                    v-bind="attrs"
                    outlined
                    class="ml-2"
                    type="submit"
                    depressed
                    >OK</v-btn
                  >
                </div>
              </template>
              <span>{{
                okButtonDisabled
                  ? "Filteret er allerede aktivert med valgt verdi."
                  : ""
              }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { FilterTitle } from "@/components/index";
import FilterDescription from "./FilterDescription";
import { getQueryStringForRange } from "./utils.js";
import FilterSettings from "./FilterSettings";

export default {
  name: "FilterRange",
  components: { FilterTitle, FilterDescription, FilterSettings },
  computed: {
    okButtonDisabled: function() {
      switch (this.filterMode.value.id) {
        case "range":
          return (
            (this.textInputMax === null && this.textInputMin === null) ||
            (this.textInputMin == this.setValues[0] &&
              this.textInputMax == this.setValues[1] &&
              !this.modeChanged)
          );
        case "greaterThanOrEqual":
          return (
            this.textInputMin === null ||
            (this.textInputMin == this.setValues[0] && !this.modeChanged)
          );
        case "lessThanOrEqual":
          return (
            this.textInputMin === null ||
            (this.textInputMin == this.setValues[0] && !this.modeChanged)
          );
        case "equals":
          return (
            this.textInputMin === null ||
            (this.textInputMin == this.setValues[0] && !this.modeChanged)
          );
        case "notEqual":
          return (
            this.textInputMin === null ||
            (this.textInputMin == this.setValues[0] && !this.modeChanged)
          );
      }
      return false;
    },
    fromTextFieldMax: function() {
      if (this.filterMode.value.id === "range") {
        if (this.options.decimals) {
          return parseFloat(this.textInputMax) - 0.001;
        } else {
          return parseInt(this.textInputMax) - 1;
        }
      } else {
        return this.options.max;
      }
    },
    toTextFieldMin: function() {
      if (this.textInputMin) {
        if (this.options.decimals) {
          return parseFloat(this.textInputMin) - 0.001;
        } else {
          return parseInt(this.textInputMin) - 1;
        }
      } else {
        return 0;
      }
    },
    currentMode: function() {
      return this.filterMode.value.id;
    },
  },
  data() {
    return {
      textInputMin: null,
      textInputMax: null,
      modeChanged: false,
      showSettings: false,
      showDescription: false,
      filterIsActive: false,
      setValues: [],
      filterMode: {
        value: {
          id: "range",
          name: "Mellom (min/maks)",
          operator: "IN",
        },
        options: [
          {
            id: "range",
            name: "Mellom (min/maks)",
            operator: "IN",
          },
          {
            id: "greaterThanOrEqual",
            name: "Er større eller lik",
            operator: ">=",
          },
          {
            id: "lessThanOrEqual",
            name: "Er mindre eller lik",
            operator: "<=",
          },
          {
            id: "equals",
            name: "Er lik",
            operator: "=",
          },
          {
            id: "notEqual",
            name: "Er ulik",
            operator: "<>",
          },
        ],
      },
    };
  },
  props: {
    title: String,
    options: Object,
    startParameter: String,
    description: String,
    endParameter: String,
    isFeltEgenskap: Boolean,
    showCheckbox: Boolean,
    filterActive: {
      type: Boolean,
      default: false,
    },
    standaloneFilter: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    currentMode(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.textInputMin = null;
        this.textInputMax = null;
        this.modeChanged = true;
      }
    },
  },
  created: function() {
    this.filterIsActive = this.filterActive;
    this.$eventHub.$on("reset-all-filters", this.hideFilter);
    this.$eventHub.$on("reset-gis-filters", () => {
      if (this.isFeltEgenskap) {
        this.hideFilter();
      }
    });
  },
  methods: {
    ...mapActions(["setFilterValue"]),
    submitFilter: function() {
      let newValue = [
        parseFloat(this.textInputMin ? this.textInputMin : "0"),
        parseFloat(
          this.textInputMax ? this.textInputMax : "" + this.options.max + ""
        ),
      ];

      this.modeChanged = false;
      this.setValues = newValue;
      this.updateFilter(newValue);
    },
    selectModeChanged: function(newValue) {
      this.filterMode.value = newValue;
    },
    filterActiveChanged: function() {
      if (!this.filterIsActive) {
        this.hideFilter();
      }
    },
    updateFilter: function(newValue) {
      if (newValue === null) {
        this.setFilterValueInStore(null);
        return;
      }

      this.textInputMin = newValue[0];
      this.textInputMax = newValue[1];

      if (
        this.filterMode.value.id === "range" &&
        newValue[0] == 0 &&
        newValue[1] == this.options.max
      ) {
        this.setFilterValueInStore(null);
      } else {
        let queryString = getQueryStringForRange(newValue, {
          startParameter: this.startParameter,
          endParameter: this.endParameter,
          operator: this.filterMode.value.operator,
          mode: this.filterMode.value.id,
        });
        this.setFilterValueInStore(queryString);
      }
    },
    setFilterValueInStore: function(newQueryString) {
      this.setFilterValue({
        filterFieldName: this.startParameter,
        filterQueryString: newQueryString,
      });
    },
    resetFilterFromButton: function() {
      this.resetFilter();
      this.updateFilter(null);
    },
    hideFilter: function() {
      if (this.showCheckbox) {
        this.filterIsActive = false;
      }
      this.resetFilter();
      this.updateFilter(null);
    },
    resetFilter: function() {
      this.setValues[0] = null;
      this.textInputMin = null;
      this.textInputMax = null;
      this.filterMode.value = this.filterMode.options[0];
      this.setValues[1] = null;
      this.$forceUpdate();
    },
  },
  mounted: function() {
    this.setValues = this.options.value;
  },
};
</script>

<style>
.rangeslide-filter {
  max-width: 400px;
  margin-bottom: 0px;
}

.filter-range-width-standalone {
  max-width: 330px;
}

.filter-range-width {
  max-width: 305px;
}

.standalone-filter {
  margin-left: 10px;
  margin-top: 20px;
  margin-bottom: 10px;
  margin-right: 20px;
  padding-left: 10px;
  padding-top: 0px;
  border-radius: 5px;
}

.range-filter-checkbox:not(.v-input--is-focused) .v-input__slot {
  border-width: 4px 4px 4px 4px;
  border-color: #eeeeee;
  border-style: solid;
}
</style>
